/**
 * SoleadLab 2.0 - Photo Blob Store
 *
 * Module-scoped Map that stores local file blobs for preview and later
 * publish-upload. Not in Zustand because File/blob objects aren't serializable.
 *
 * Matches original editor (23).html photoBlobs Map behavior.
 */

import { devRelativize } from '../services/signedAssetUrl';

export interface PhotoBlobEntry {
  file: File;
  url: string; // URL.createObjectURL result
  mime: string;
}

// Module-scoped store
const photoBlobs = new Map<string, PhotoBlobEntry>();

/** Sanitize a filename for path safety */
function sanitizeFilename(name: string): string {
  const base = String(name || 'photo').split(/[\\/]/).pop() || 'photo';
  return base.replace(/[^a-zA-Z0-9._-]+/g, '_');
}

/** Generate a unique relative path like "photos/IMG_0001.JPG" */
export function uniqueRelPhotoPath(originalName: string): string {
  const base = sanitizeFilename(originalName || 'photo.jpg');
  const rel = 'photos/' + base;
  if (!photoBlobs.has(rel)) return rel;

  const m = base.match(/^(.*?)(\.[^.]+)?$/);
  const stem = m ? m[1] : base;
  const ext = m && m[2] ? m[2] : '';
  let k = 2;
  while (photoBlobs.has(`photos/${stem}_${k}${ext}`)) k++;
  return `photos/${stem}_${k}${ext}`;
}

/** Store a file blob, returning the blob URL for display */
export function storePhotoBlob(relPath: string, file: File): string {
  const url = URL.createObjectURL(file);
  photoBlobs.set(relPath, { file, url, mime: file.type || 'image/jpeg' });
  return url;
}

/**
 * Resolve a relative path (like "photos/IMG_001.JPG") to a displayable URL.
 * Falls through: blob lookup → blob:/data: passthrough → raw path.
 *
 * Remote `https://api.soleadlab.com/...` URLs (the post-publish form) get
 * dev-relativized so they route through the Vite proxy in `vite`/`electron:dev`,
 * where the renderer lives on `localhost:5173` and the SameSite=Lax cookie
 * wouldn't otherwise ship cross-site to api.soleadlab.com. Prod is untouched.
 */
export function resolveImageSrc(path: string): string {
  const p = (path || '').trim();
  if (!p) return '';

  // Direct lookup
  const entry = photoBlobs.get(p);
  if (entry?.url) return entry.url;

  // blob: or data: passthrough
  if (p.startsWith('blob:') || p.startsWith('data:')) return p;

  // Filename search fallback
  const filename = p.includes('/') ? p.split('/').pop() : '';
  if (filename) {
    for (const [key, val] of photoBlobs.entries()) {
      if (key.endsWith(filename) || key.includes(filename)) {
        if (val?.url) return val.url;
      }
    }
  }

  // Remote URL after publish (or any other passthrough) — dev-relativize so
  // <img>/<video> tags use the Vite proxy in dev. No-op in prod.
  return devRelativize(p);
}

/** Get a specific blob entry by relative path (for thermal parsing, etc.) */
export function getPhotoBlobEntry(relPath: string): PhotoBlobEntry | undefined {
  return photoBlobs.get(relPath);
}

/** Get all stored blobs (for ZIP export / publish upload) */
export function getAllPhotoBlobs(): Map<string, PhotoBlobEntry> {
  return photoBlobs;
}

/** Remove a specific blob entry, revoking its object URL */
export function removePhotoBlob(relPath: string): void {
  const entry = photoBlobs.get(relPath);
  if (entry?.url) URL.revokeObjectURL(entry.url);
  photoBlobs.delete(relPath);
}

/** Clear all blobs (project close / clear) */
export function clearAllPhotoBlobs(): void {
  for (const entry of photoBlobs.values()) {
    if (entry.url) URL.revokeObjectURL(entry.url);
  }
  photoBlobs.clear();
}
